import { useCallback, useEffect, useState } from 'react';
import { RoleCode, type HonorPegawai, type PegawaiListItem } from '@app/shared';
import { AdminLayout } from '../../components/admin-layout';
import { Alert, Button, Card, Input, Pagination, Select, Table, Td, Th } from '../../components/ui';
import { useAuth } from '../../features/auth/use-auth';
import {
  createHonorPegawai,
  deleteHonorPegawai,
  getHonorPegawaiList,
  getPegawaiList,
  updateHonorPegawai,
} from '../../features/master/api';

const today = () => new Date().toISOString().slice(0, 10);
const rupiah = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
});

type HonorForm = {
  pegawai_id: string;
  tarif_honor_per_jp: string;
  tarif_kehadiran_per_hari: string;
  honor_jabatan: string;
  honor_wali_kelas: string;
  berlaku_mulai: string;
  berlaku_sampai: string;
};

const emptyForm = (): HonorForm => ({
  pegawai_id: '',
  tarif_honor_per_jp: '0',
  tarif_kehadiran_per_hari: '0',
  honor_jabatan: '0',
  honor_wali_kelas: '0',
  berlaku_mulai: today(),
  berlaku_sampai: '',
});

function formFrom(row: HonorPegawai): HonorForm {
  return {
    pegawai_id: String(row.pegawai_id),
    tarif_honor_per_jp: String(row.tarif_honor_per_jp),
    tarif_kehadiran_per_hari: String(row.tarif_kehadiran_per_hari),
    honor_jabatan: String(row.honor_jabatan),
    honor_wali_kelas: String(row.honor_wali_kelas),
    berlaku_mulai: row.berlaku_mulai,
    berlaku_sampai: row.berlaku_sampai ?? '',
  };
}

export function HonorPegawaiPage() {
  const { user } = useAuth();
  const canManage = user?.roles.includes(RoleCode.ADMIN) ?? false;
  const [rows, setRows] = useState<HonorPegawai[]>([]);
  const [pegawai, setPegawai] = useState<PegawaiListItem[]>([]);
  const [meta, setMeta] = useState({ page: 1, total_pages: 1 });
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<HonorPegawai | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<HonorForm>(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async (page = 1) => {
      try {
        const [honorResult, pegawaiResult] = await Promise.all([
          getHonorPegawaiList({ page, limit: 10, search: search || undefined }),
          getPegawaiList({ limit: 100, status_aktif: true }),
        ]);
        setRows(honorResult.rows);
        setMeta({ page: honorResult.meta.page, total_pages: honorResult.meta.total_pages });
        setPegawai(pegawaiResult.rows);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Gagal memuat konfigurasi honor');
      }
    },
    [search],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (row: HonorPegawai) => {
    setEditing(row);
    setForm(formFrom(row));
    setShowForm(true);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const values = {
      tarif_honor_per_jp: Number(form.tarif_honor_per_jp),
      tarif_kehadiran_per_hari: Number(form.tarif_kehadiran_per_hari),
      honor_jabatan: Number(form.honor_jabatan),
      honor_wali_kelas: Number(form.honor_wali_kelas),
      berlaku_mulai: form.berlaku_mulai,
      berlaku_sampai: form.berlaku_sampai || null,
    };
    if (
      !form.pegawai_id ||
      Object.values(values).some(
        (value) => typeof value === 'number' && (!Number.isInteger(value) || value < 0),
      )
    ) {
      setError('Pegawai wajib dipilih; semua tarif harus bilangan bulat non-negatif');
      return;
    }
    setSaving(true);
    try {
      if (editing) await updateHonorPegawai(editing.id, values);
      else await createHonorPegawai({ ...values, pegawai_id: Number(form.pegawai_id) });
      setShowForm(false);
      await load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menyimpan konfigurasi honor');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Hapus konfigurasi honor ini?')) return;
    try {
      await deleteHonorPegawai(id);
      await load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menghapus konfigurasi honor');
    }
  };

  return (
    <AdminLayout title="Pengaturan Honor Pegawai">
      {error && <Alert type="error">{error}</Alert>}
      {showForm && canManage && (
        <Card>
          <h3>{editing ? 'Ubah Pengaturan Honor' : 'Tambah Pengaturan Honor'}</h3>
          <form onSubmit={submit}>
            <Select
              label="Pegawai"
              value={form.pegawai_id}
              disabled={Boolean(editing)}
              onChange={(e) => setForm({ ...form, pegawai_id: e.target.value })}
              required
            >
              <option value="">Pilih pegawai</option>
              {pegawai.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.nip_nik} — {row.nama}
                </option>
              ))}
            </Select>
            <Input
              label="Tarif Honor per JP"
              type="number"
              min="0"
              step="1"
              value={form.tarif_honor_per_jp}
              onChange={(e) => setForm({ ...form, tarif_honor_per_jp: e.target.value })}
              required
            />
            <Input
              label="Tarif Kehadiran per Hari"
              type="number"
              min="0"
              step="1"
              value={form.tarif_kehadiran_per_hari}
              onChange={(e) => setForm({ ...form, tarif_kehadiran_per_hari: e.target.value })}
              required
            />
            <Input
              label="Honor Jabatan"
              type="number"
              min="0"
              step="1"
              value={form.honor_jabatan}
              onChange={(e) => setForm({ ...form, honor_jabatan: e.target.value })}
              required
            />
            <Input
              label="Honor Wali Kelas"
              type="number"
              min="0"
              step="1"
              value={form.honor_wali_kelas}
              onChange={(e) => setForm({ ...form, honor_wali_kelas: e.target.value })}
              required
            />
            <Input
              label="Berlaku Mulai"
              type="date"
              value={form.berlaku_mulai}
              onChange={(e) => setForm({ ...form, berlaku_mulai: e.target.value })}
              required
            />
            <Input
              label="Berlaku Sampai"
              type="date"
              value={form.berlaku_sampai}
              min={form.berlaku_mulai}
              onChange={(e) => setForm({ ...form, berlaku_sampai: e.target.value })}
            />
            <Button type="submit" disabled={saving}>
              Simpan
            </Button>{' '}
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
              Batal
            </Button>
          </form>
        </Card>
      )}
      <Card>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <Input
            placeholder="Cari nama/NIP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          {canManage && <Button onClick={openCreate}>Tambah Pengaturan</Button>}
        </div>
        <Table>
          <thead>
            <tr>
              <Th>Pegawai</Th>
              <Th>Honor/JP</Th>
              <Th>Kehadiran/Hari</Th>
              <Th>Jabatan</Th>
              <Th>Wali Kelas</Th>
              <Th>Periode</Th>
              {canManage && <Th>Aksi</Th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <Td>
                  {row.nip_nik} — {row.nama}
                </Td>
                <Td>{rupiah.format(row.tarif_honor_per_jp)}</Td>
                <Td>{rupiah.format(row.tarif_kehadiran_per_hari)}</Td>
                <Td>{rupiah.format(row.honor_jabatan)}</Td>
                <Td>{rupiah.format(row.honor_wali_kelas)}</Td>
                <Td>
                  {row.berlaku_mulai} s.d. {row.berlaku_sampai ?? 'seterusnya'}
                </Td>
                {canManage && (
                  <Td>
                    <Button variant="secondary" onClick={() => openEdit(row)}>
                      Ubah
                    </Button>{' '}
                    <Button variant="danger" onClick={() => void remove(row.id)}>
                      Hapus
                    </Button>
                  </Td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <Td colSpan={canManage ? 7 : 6} style={{ textAlign: 'center' }}>
                  Tidak ada data
                </Td>
              </tr>
            )}
          </tbody>
        </Table>
        <Pagination
          page={meta.page}
          totalPages={meta.total_pages}
          onChange={(page) => void load(page)}
        />
      </Card>
    </AdminLayout>
  );
}
